import { VStack, Stack, Container, HStack, Center, Spinner } from '@chakra-ui/react';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useGetPrototypesQuery } from 'hooks/queries/prototypes/useGetPrototypesQuery';
import { useEffect } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import CodePreviewCard from 'components/CodePreviewCard';
import { EmptyContent } from 'components/EmptyContent';
import PageLoader from 'components/PageLoader';
import Text from 'components/Text';
import { CodePreview } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';

const SolutionsTab = () => {
  const { id: currentUserId } = useCurrentUser();

  const { data, isFetching, ref } = useGetPrototypesQuery({ userId: null });

  const queryClient = useQueryClient();

  const { refetch } = useGetPrototypesQuery({ userId: currentUserId });

  useEffect(() => {
    queryClient.removeQueries({ queryKey: [TanstackQueryName.GetPrototypes, currentUserId] });
    refetch({ userId: currentUserId });
  }, []);

  const prototypesData = (data as unknown as InfiniteData<CodePreview[]>) || [];
  const prototypes =
    prototypesData.pages.reduce((acc, page) => acc.concat(page), [] as CodePreview[]) || [];

  const renderContent = () => {
    const isFirstPage = prototypesData.pages.length === 0;

    if (isFirstPage && isFetching) {
      return <PageLoader shift />;
    }

    if (prototypes?.length === 0) {
      return <EmptyContent type="prototype" />;
    }

    return (
      <>
        <Stack spacing={4} width="100%" pr={2}>
          {prototypes.map((userCode) => (
            <CodePreviewCard
              key={userCode.id}
              isMySolution={false}
              canEdit={false}
              containerHeight="600px"
              queryKey={[TanstackQueryName.GetPrototypes, null]}
              invalidateKeys={[
                [TanstackQueryName.GetPrototypes, currentUserId],
                [TanstackQueryName.GetPrototype, userCode.solution_url!],
                [TanstackQueryName.GetProfileCode, userCode.user_id!, 'prototype'],
                [TanstackQueryName.GetUserCode, currentUserId, 0],
                [TanstackQueryName.GetUserCode, currentUserId, 1],
                [TanstackQueryName.GetUserCode, currentUserId, 2],
              ]}
              {...userCode}
            />
          ))}
        </Stack>
        <div ref={ref} />
        <Center width="100%" mt={10}>
          {isFetching && !isFirstPage && <Spinner size="lg" />}
        </Center>
      </>
    );
  };

  return (
    <>
      <Container maxW="1400px">
        <VStack alignItems="flex-start" spacing={4} width="100%" pb={4}>
          <HStack width="100%" justifyContent="space-between">
            <Text fontSize="xxLarge" fontWeight="bold" pt={4}>
              Explore
            </Text>
          </HStack>
          {renderContent()}
        </VStack>
      </Container>
    </>
  );
};

export default SolutionsTab;
